import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useReportes } from './ReportesContex';
import Chart from 'react-apexcharts';
import { PhoneCall, Clock, Urgent, Hourglass } from 'tabler-icons-react';
import '../../styles/reportes.css';

function getPrimaryColor() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--color').trim();
    return raw.startsWith('#') ? raw.slice(1) : raw;
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    const [r1, g1, b1] = parse(hex.padStart(6, '0'));
    const [r2, g2, b2] = parse(blendWith.padStart(6, '0'));
    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

// Escala de color por ranking: la #1 con el tono más intenso, descendiendo hacia un tono claro.
function rampColors(n, base, light) {
    if (n <= 1) return [`#${base}`];
    return Array.from({ length: n }, (_, i) => {
        const ratio = 1 - (i / (n - 1)) * 0.62;
        return `#${blendHex(base, light, ratio)}`;
    });
}

// Convierte minutos a una cadena legible: min < 60 → "X min"; < 48 h → "X h"; resto → "Xd Yh" o "X.Xk h".
function formatDuracion(min) {
    if (!min || min <= 0) return '0 min';
    if (min < 60) return `${Math.round(min)} min`;
    const horas = min / 60;
    if (horas < 48) return `${horas.toFixed(1)} h`;
    if (horas < 10000) {
        const dias = Math.floor(horas / 24);
        const restoH = Math.round(horas % 24);
        return `${dias.toLocaleString()}d ${restoH}h`;
    }
    return `${(horas / 1000).toFixed(1)}k h`;
}

// Hook de conteo animado (ease-out cubic).
function useCountUp(target, duration = 900) {
    const [val, setVal] = useState(0);
    const rafRef = useRef();
    useEffect(() => {
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            setVal(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, duration]);
    return val;
}

async function exportarExcel(topIncidencias, topDuracion, rangoTxt, filtroTxt) {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const toArgb = (hex) => `FF${hex.toUpperCase()}`;
    const primary = getPrimaryColor();
    const headerBg = blendHex(primary, '000000', 0.8);
    const infoBg = blendHex(primary, 'ffffff', 0.85);

    const wb = new ExcelJS.Workbook();

    const buildSheet = (name, rows, valLabel) => {
        const ws = wb.addWorksheet(name);

        const infoText = [rangoTxt ? `Rango: ${rangoTxt}` : '', filtroTxt].filter(Boolean).join('   |   ');
        if (infoText) {
            ws.mergeCells('A1:C1');
            ws.getCell('A1').value = infoText;
            ws.getCell('A1').style = { font: { size: 11 }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(infoBg) } } };
            ws.getRow(1).height = 18;
        }

        const hdrStyle = { font: { bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(headerBg) } }, alignment: { horizontal: 'center', vertical: 'middle' } };
        const hdrs = ['#', 'Llamada', valLabel];
        const hdrRowIdx = infoText ? 2 : 1;
        const hdrRow = ws.getRow(hdrRowIdx);
        hdrs.forEach((h, i) => { hdrRow.getCell(i + 1).value = h; hdrRow.getCell(i + 1).style = hdrStyle; });
        hdrRow.height = 20;
        [6, 48, 18].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

        rows.forEach((item, i) => {
            const row = ws.getRow(hdrRowIdx + 1 + i);
            row.getCell(1).value = i + 1;
            row.getCell(2).value = item.nombre;
            row.getCell(3).value = item.total;
            row.getCell(3).numFmt = '#,##0';
            if (i % 2 !== 0) {
                row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F8FE' } }; });
            }
            row.height = 16;
        });
    };

    buildSheet('Incidencias', topIncidencias, 'Incidencias');
    buildSheet('Duración (min)', topDuracion, 'Duración (min)');

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `top_llamadas_${new Date().toISOString().slice(0, 10)}.xlsx`);
}


function chartOptions({ categories, colors, valLabel, isDuracion }) {
    return {
        chart: {
            type: 'bar',
            toolbar: { show: false },
            zoom: { enabled: false },
            parentHeightOffset: 0,
            fontFamily: 'WorkSans',
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 700,
                animateGradually: { enabled: true, delay: 55 },
                dynamicAnimation: { enabled: true, speed: 400 },
            },
        },
        colors,
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'light',
                type: 'horizontal',
                shadeIntensity: 0.25,
                gradientToColors: undefined,
                opacityFrom: 1,
                opacityTo: 0.92,
                stops: [0, 100],
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                distributed: true,
                borderRadius: 4,
                borderRadiusApplication: 'end',
                barHeight: '64%',
                dataLabels: { position: 'top' },
            },
        },
        dataLabels: {
            enabled: true,
            textAnchor: 'start',
            offsetX: 8,
            formatter: (val) => (isDuracion ? formatDuracion(val) : Number(val).toLocaleString()),
            style: {
                fontSize: '0.75rem',
                fontFamily: 'WorkSans',
                fontWeight: 700,
                colors: ['#13151c'],
            },
        },
        legend: { show: false },
        states: {
            hover: { filter: { type: 'darken', value: 0.9 } },
        },
        xaxis: {
            categories,
            labels: {
                formatter: (val) => (isDuracion ? formatDuracion(val) : Number(val).toLocaleString()),
                style: { colors: '#5f606f', fontFamily: 'WorkSans', fontSize: '0.75rem' },
            },
        },
        yaxis: {
            labels: {
                maxWidth: 480,
                style: { colors: '#5f606f', fontFamily: 'WorkSans', fontSize: '0.75rem' },
            },
        },
        grid: {
            borderColor: '#e9ecf0',
            padding: { right: 64, bottom: 0 },
        },
        tooltip: {
            y: {
                title: { formatter: () => `${valLabel}:` },
                formatter: (val) => (isDuracion ? `${formatDuracion(val)}  ·  ${Number(val).toLocaleString()} min` : `${Number(val).toLocaleString()}`),
            },
        },
        noData: {
            text: 'No existen datos para esta selección',
            align: 'center',
            verticalAlign: 'middle',
            style: { fontSize: '1.125rem', fontFamily: 'WorkSans', fontWeight: 600, color: '#5f606f' },
        },
    };
}

function KpiCard({ icon, iconClass, value, label }) {
    const shown = useCountUp(value);
    return (
        <div className="pareto-kpi-card">
            <div className={`pareto-kpi-card-icon ${iconClass}`}>{icon}</div>
            <div className="pareto-kpi-card-body">
                <span className="pareto-kpi-card-value">{Math.round(shown).toLocaleString()}</span>
                <span className="pareto-kpi-card-label pareto-kpi-card-label--wrap">{label}</span>
            </div>
        </div>
    );
}

function KpiCardText({ icon, iconClass, value, label }) {
    return (
        <div className="pareto-kpi-card">
            <div className={`pareto-kpi-card-icon ${iconClass}`}>{icon}</div>
            <div className="pareto-kpi-card-body">
                <span className="pareto-kpi-card-value">{value}</span>
                <span className="pareto-kpi-card-label pareto-kpi-card-label--wrap">{label}</span>
            </div>
        </div>
    );
}

export default function TopLlamadas() {
    const {
        topLlamadas,
        txtRange,
        selectedArea,
        selectedLinea,
        selectedCelda,
        filteredLines,
        setSelectedLinea,
        setExportFn,
    } = useReportes();

    // Por defecto seleccionar la primera línea de la lista (no "Todas").
    useEffect(() => {
        if (!selectedLinea && filteredLines.length > 0) {
            setSelectedLinea(filteredLines[0]);
        }
    }, [filteredLines, selectedLinea, setSelectedLinea]);

    const { topIncidencias, topDuracion } = topLlamadas;

    const incidencias = useMemo(() => ({
        categories: topIncidencias.map((i) => i.nombre),
        series: [{ name: 'Incidencias', data: topIncidencias.map((i) => i.total) }],
        colors: rampColors(topIncidencias.length, '3b5bdb', 'cdddf7'),
    }), [topIncidencias]);

    const duracion = useMemo(() => ({
        categories: topDuracion.map((i) => i.nombre),
        series: [{ name: 'Duración', data: topDuracion.map((i) => i.total) }],
        colors: rampColors(topDuracion.length, '5c7cbb', 'e1e8f3'),
    }), [topDuracion]);

    const kpis = useMemo(() => {
        const incTotal = topIncidencias.reduce((a, i) => a + (i.total || 0), 0);
        const durTotalMin = topDuracion.reduce((a, i) => a + (i.total || 0), 0);
        return {
            incTotal,
            durTotalMin,
            topInc: topIncidencias[0] || null,
            topDur: topDuracion[0] || null,
        };
    }, [topIncidencias, topDuracion]);

    const filtroTxt = useMemo(() => [
        selectedArea?.nombre ? `Área: ${selectedArea.nombre}` : 'Todas las áreas',
        selectedLinea?.nombre ? `Línea: ${selectedLinea.nombre}` : null,
        selectedCelda?.nombre ? `Celda: ${selectedCelda.nombre}` : null,
    ].filter(Boolean).join('   ·   '), [selectedArea, selectedLinea, selectedCelda]);

    const nombreFiltro = selectedCelda?.nombre || selectedLinea?.nombre || selectedArea?.nombre || '';

    const hayDatos = topIncidencias.length > 0 || topDuracion.length > 0;

    useEffect(() => {
        if (hayDatos) {
            setExportFn(() => () => exportarExcel(topIncidencias, topDuracion, txtRange, filtroTxt));
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [hayDatos, topIncidencias, topDuracion, txtRange, filtroTxt, setExportFn]);

    const altura = (count) => `${Math.max(280, count * 40 + 90)}px`;

    return (
        <div className="tiempos-container">
            {hayDatos && (
                <div className="pareto-kpi-row">
                    <KpiCard
                        icon={<PhoneCall size={20} strokeWidth={2} />}
                        iconClass="pareto-kpi-card-icon--blue"
                        value={kpis.incTotal}
                        label="Incidencias totales"
                    />
                    {kpis.topInc && (
                        <KpiCardText
                            icon={<Urgent size={20} strokeWidth={2} />}
                            iconClass="pareto-kpi-card-icon--red"
                            value={kpis.topInc.total.toLocaleString()}
                            label={`más frecuente · ${kpis.topInc.nombre}`}
                        />
                    )}
                    <KpiCardText
                        icon={<Hourglass size={20} strokeWidth={2} />}
                        iconClass="pareto-kpi-card-icon--green"
                        value={formatDuracion(kpis.durTotalMin)}
                        label="tiempo total acumulado"
                    />
                    {kpis.topDur && (
                        <KpiCardText
                            icon={<Clock size={20} strokeWidth={2} />}
                            iconClass="pareto-kpi-card-icon--orange"
                            value={formatDuracion(kpis.topDur.total)}
                            label={`más larga · ${kpis.topDur.nombre}`}
                        />
                    )}
                </div>
            )}

            <div className="tiempos-chart-card">
                <p className="tiempos-chart-title">
                    Llamadas con Mayor Incidencia{nombreFiltro ? ` · ${nombreFiltro}` : ''}
                </p>
                <Chart
                    options={chartOptions({ categories: incidencias.categories, colors: incidencias.colors, valLabel: 'Incidencias', isDuracion: false })}
                    series={incidencias.series}
                    type="bar"
                    width="100%"
                    height={altura(topIncidencias.length)}
                />
                <p className="top-llamadas-axis-label">Incidencias</p>
            </div>

            <div className="tiempos-chart-card">
                <p className="tiempos-chart-title">
                    Llamadas con Mayor Duración{nombreFiltro ? ` · ${nombreFiltro}` : ''}
                </p>
                <Chart
                    options={chartOptions({ categories: duracion.categories, colors: duracion.colors, valLabel: 'Duración', isDuracion: true })}
                    series={duracion.series}
                    type="bar"
                    width="100%"
                    height={altura(topDuracion.length)}
                />
                <p className="top-llamadas-axis-label">Duración acumulada</p>
            </div>
        </div>
    );
}
